import { Select, Spin } from 'antd';
import Styles from './index.module.scss';
import { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import {
  clearSearchHistory,
  getSearchHistory,
  setSearchHistory,
} from './utils';
import { searchDocs } from '@/services/document-next';
import { useUserAgentInfo } from '@/context/user-agent';
import useWebLogger from '@/hooks/useWebLogger';
import { formEvent } from '@/utils/const/weblogger/event';
import { gotoArticleByProduct } from '@/utils/document/article-and-product';
import SearchIcon from '@/components/SvgIcon/icons/SearchIcon';
import DeleteIcon from '@/components/SvgIcon/icons/DeleteIcon';
import Link from 'next/link';
interface IProps {
  width?: number;
  height?: number;
  placeholder?: string;
  hotSearchKeys?: string[];
}

interface ISearchDocRes {
  products: {
    id: number;
    productName: string;
    productCode: string;
  }[];
  docs: {
    productId: number;
    docId: string;
    productName: string;
    productCode: string;
    docName: string;
  }[];
}

const DocSearchTool = ({
  width = 550,
  height = 42,
  placeholder = '请输入关键词搜索产品文档，例如 点播云',
  hotSearchKeys,
}: IProps) => {
  const [isFocus, setIsFocus] = useState(false);
  const [searchContent, setSearchContent] = useState<{
    products: any[];
    docs: any[];
  }>();
  const [searchKey, setSearchKey] = useState<string>();
  const [loading, setLoading] = useState<boolean>();
  const { clickEventRef } = useWebLogger();
  const UAInfo = useUserAgentInfo();

  const selectRef = useRef(null);
  const searchKeyClick = (val) => {
    let valTrim = val?.trim();
    if (valTrim) {
      setSearchKey(valTrim);
    } else {
      setSearchKey(null);
    }
  };
  const onSearch = debounce(searchKeyClick, 1000);

  useEffect(() => {
    if (searchKey) {
      searchDocsByKey(searchKey);
    } else {
      setSearchContent(null);
    }
    setHistorySearchKeys(() => getSearchHistory());
  }, [searchKey]);
  const searchDocsByKey = async (key) => {
    setLoading(true);
    try {
      const res: ISearchDocRes = await searchDocs(key);
      setSearchHistory(key);
      setSearchContent(res);
    } catch {}
    setLoading(false);

    // 埋点上报
    clickEventRef.current({
      action: formEvent.CMSSearchTool,
      params: {
        key,
        userAgent: UAInfo,
      },
    });
  };
  const [historySearchKeys, setHistorySearchKeys] = useState(() =>
    getSearchHistory()
  );
  const clearSearchHistoryHandle = () => {
    const temp = clearSearchHistory();
    setHistorySearchKeys(temp);
  };
  const handleProductClick = async (productId) => {
    await gotoArticleByProduct({ productId }, false);
  };

  // const gotDocDetail
  // 自定义渲染下拉框
  const dropdownRender = () => {
    if (searchKey) {
      return (
        <div className={Styles['search-content']}>
          <Spin spinning={loading}>
            {/* 暂不展示相关产品 */}
            {/* <div className={Styles['title']}>相关产品</div>
            {searchContent?.products.length ? (
              searchContent.products.map((item) => (
                <span
                  key={item.productCode}
                  className={Styles['product-tag']}
                  onClick={() => {
                    handleProductClick(item.id);
                  }}
                >
                  {item.productName}
                </span>
              ))
            ) : (
              <div className={Styles['empty-tooltip']}>暂无相关产品</div>
            )} */}
            <div className={Styles['title']}>相关文档</div>
            {searchContent?.docs.length ? (
              searchContent.docs.map((item) => (
                <div key={item.docId} className={Styles['doc']}>
                  <Link
                    href={`/document/${item.productCode}/${item.docId}`}
                    passHref
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span className={Styles['product-prefix']}>
                      {item.productName}
                    </span>
                    {`${item.docName}`}
                  </Link>
                </div>
              ))
            ) : (
              <div className={Styles['empty-tooltip']} style={{ height: 160 }}>
                文档建设中，敬请期待...
              </div>
            )}
          </Spin>
        </div>
      );
    }
    return (
      <div className={`${Styles['search-content']}`}>
        <div className={Styles['search-content-empty-history']}>
          <div className={Styles['search-content-empty-title']}>
            历史搜索
            <DeleteIcon
              className={Styles['delete-icon']}
              onClick={clearSearchHistoryHandle}
            />
          </div>
          {historySearchKeys.length ? (
            historySearchKeys.map((key, index) => (
              <span
                className={Styles['search-key']}
                key={`${key}${index}`}
                onClick={() => searchKeyClick(key)}
              >
                {key}
              </span>
            ))
          ) : (
            <span>暂无搜索记录</span>
          )}
        </div>
        {hotSearchKeys && hotSearchKeys.length ? (
          <HotKeys />
        ) : (
          <div>请输入关键字搜索</div>
        )}
      </div>
    );
  };

  const HotKeys = () => {
    return (
      <div className={Styles['hot-keys']}>
        <div className={Styles['search-content-empty-title']}>热门关键词</div>
        {hotSearchKeys.map((item) => (
          <span
            className={Styles['search-key']}
            key={item}
            onClick={() => searchKeyClick(item)}
          >
            {item}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className={Styles['doc-search-tool-container']}>
      <span
        className={`${Styles['search-icon']} ${
          isFocus ? Styles['search-icon-focus'] : ''
        }`}
      >
        <SearchIcon />
      </span>
      <Select
        ref={selectRef}
        allowClear
        onClear={() => {
          setSearchKey(null);
        }}
        listHeight={320}
        size="large"
        style={{ width, height }}
        showArrow={false}
        showSearch
        onDropdownVisibleChange={(open) => {
          setIsFocus(open);
        }}
        placeholder={placeholder}
        dropdownRender={dropdownRender}
        onSearch={(val) => {
          if (val) {
            onSearch(val);
          }
        }}
        value={searchKey}
      ></Select>
      {hotSearchKeys && hotSearchKeys.length && (
        <div className={Styles['hot-keys-container']}>
          <div>
            <span>热门关键词：</span>
            {hotSearchKeys.map((item) => (
              <span
                className={Styles['hot-key']}
                key={item}
                onClick={() => searchKeyClick(item)}
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocSearchTool;
